import { Popover } from '@mui/material';
import React, { useState, type ReactNode } from 'react';
import { getLangValue, type Language } from '../api/Lang';
import { AllergenIconImg } from '../api/allergenIcon';
import type { Allergens, AllergensList } from '../types';

/** #popup .filler uses z-index 9999 — MUI Menu/Popover default modal z-index (1300) renders behind it. */
export const MEALS_POPUP_OVERLAY_Z_INDEX = 10001;

export function MealPopupCategory({ heading, subHeading }: { heading: string; subHeading: string }) {
    return <div>
        <div className='popup-heading-meals'>{heading}</div>
        <div className={'menu-description'}>{subHeading}</div>
    </div>;
}

export function getMealAllergenLabels(
    mealAllergens: AllergensList | undefined,
    allergensCatalog: Allergens | undefined,
    lang: Language,
): string[] {
    if (!mealAllergens) return [];
    const labels: string[] = [];
    Object.keys(mealAllergens).forEach((key) => {
        if (key === 'foodIntolerances') return;
        if (mealAllergens[key]) {
            const name = allergensCatalog?.[key] ? getLangValue(allergensCatalog[key], lang) : key;
            if (name) labels.push(name);
        }
    });
    if (mealAllergens.foodIntolerances) {
        labels.push(mealAllergens.foodIntolerances);
    }
    return labels;
}

export function MealAllergensIndicator({ labels, buttonLabel }: { labels: string[]; buttonLabel: string }) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <button
                type="button"
                className="meal-popup-allergens-btn"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-expanded={open}
            >
                <span>{buttonLabel}</span>
                <AllergenIconImg alt="" width={12} height={12} variant="green" />
            </button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: -12, horizontal: 20 }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    root: { sx: { zIndex: MEALS_POPUP_OVERLAY_Z_INDEX } },
                    paper: {
                        sx: {
                            overflow: 'visible',
                            background: 'transparent',
                            boxShadow: 'none',
                        },
                    },
                }}
            >
                <div className="meal-popup-allergens-popover">
                    <div className="meal-popup-allergens-tags">
                        {labels.map((label, index) => (
                            <span key={`${label}-${index}`} className="meal-popup-allergens-tag">{label}</span>
                        ))}
                    </div>
                    <span className="meal-popup-allergens-popover-arrow" aria-hidden="true" />
                </div>
            </Popover>
        </>
    );
}

export type MealPopupFoodItemLayoutProps = {
    hideBottomBorder?: boolean;
    selected?: boolean;
    setMenuTitle?: ReactNode;
    setMenuTitleDimmed?: boolean;
    titleDimmed?: boolean;
    title: ReactNode;
    description?: ReactNode;
    dietary?: ReactNode;
    allergens?: ReactNode;
    checkboxes?: ReactNode;
    imageUrl?: string | null;
    imageAlt?: string;
    footerPrice?: ReactNode;
    footerActions?: ReactNode;
};

export function MealPopupFoodItemLayout({
    hideBottomBorder,
    selected,
    setMenuTitle,
    setMenuTitleDimmed,
    titleDimmed,
    title,
    description,
    dietary,
    allergens,
    checkboxes,
    imageUrl,
    imageAlt,
    footerPrice,
    footerActions,
}: MealPopupFoodItemLayoutProps) {
    return (
        <div className={`meal-popup-food-item${hideBottomBorder ? ' meal-popup-food-item--no-border' : ''}`}>
            {setMenuTitle != null &&
                <div className={`set-menu-title ${setMenuTitleDimmed ? 'opac5' : ''}`}>{setMenuTitle}</div>
            }

            <div className={`meal-popup-food-item__body ${selected ? 'selected' : ''}`}>
                <div className="meal-popup-food-item__main">
                    <div className="meal-popup-food-item__info">
                        <div className="meal-popup-food-item__title-block">
                            <div className="meal-popup-food-item__title-desc">
                                <div className={`meal-popup-food-item__title ${titleDimmed ? 'opac5' : ''}`}>
                                    {title}
                                </div>
                                {description != null &&
                                    <div className="meal-popup-food-item__description">{description}</div>
                                }
                            </div>
                        </div>

                        {dietary}
                        {allergens}
                        {checkboxes}
                    </div>

                    {imageUrl &&
                        <div className="meal-popup-food-item__image">
                            <img src={imageUrl} alt={imageAlt ?? ''} />
                        </div>
                    }
                </div>

                {(footerPrice != null || footerActions != null) &&
                    <div className="meal-popup-food-item__footer">
                        {footerPrice != null &&
                            <div className={`meal-popup-food-item__price ${titleDimmed ? 'opac5' : ''}`}>
                                {footerPrice}
                            </div>
                        }
                        {footerActions != null &&
                            <div className="meal-popup-food-item__actions">{footerActions}</div>
                        }
                    </div>
                }
            </div>
        </div>
    );
}